import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, Lock, ChevronRight, Clock, Dumbbell, ArrowLeft, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { ProgramDetailModal } from "@/components/athlete/AthleteComponents";
import { useViewPreferences } from "@/components/coach/SharedComponents";

// ==================== PROGRAMS PAGE (for athletes) ====================
const ProgramsPage = () => {
  const { user } = useAuth();
  const [programs, setPrograms] = useState([]);
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [programProgress, setProgramProgress] = useState({});
  const { viewMode, setViewMode } = useViewPreferences("athlete_programs");

  useEffect(() => {
    fetchPrograms();
    fetchWorkouts();
    fetchProgress();
  }, []);

  const fetchPrograms = async () => {
    try {
      const response = await fetch(`${API}/workouts/programs/`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setPrograms(data.programs || []);
      }
    } catch (error) {
      console.error("Failed to fetch programs");
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkouts = async () => {
    try {
      const response = await fetch(`${API}/workouts/`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setWorkouts(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch workouts");
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await fetch(`${API}/workouts/programs/progress/`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        const progressMap = {};
        (data.progress || []).forEach(p => { progressMap[p.program_id] = p; });
        setProgramProgress(progressMap);
      }
    } catch (error) {
      console.error("Failed to fetch progress");
    }
  };

  const handleStartProgram = async (programId) => {
    try {
      const response = await fetch(`${API}/workouts/programs/${programId}/start`, {
        method: "POST",
        credentials: "include"
      });
      if (response.ok) {
        toast.success("Program started!");
        fetchProgress();
      }
    } catch (error) {
      toast.error("Failed to start program");
    }
  };

  /**
   * Given a program + its progress doc, compute:
   *  - lastCompleted: { week, day, title, completedAt } or null
   *  - nextUp:        { week, day, title } or null when the program is finished
   * Walks program.weeks[].days[] in order, finds the first non-rest day that
   * isn't in progress.completed_days, returns the previous done one and the
   * upcoming one.
   */
  const computeProgramStatus = (program, progress) => {
    if (!progress || !program?.weeks) return { lastCompleted: null, nextUp: null };
    const completedSet = new Set(
      (progress.completed_days || []).map(d => `${d.week}-${d.day}`)
    );
    let nextUp = null;
    for (const w of program.weeks) {
      for (const d of (w.days || [])) {
        if (d.is_rest_day) continue;
        const k = `${w.week_number}-${d.day_number}`;
        if (!completedSet.has(k)) {
          nextUp = { week: w.week_number, day: d.day_number, title: d.workout_title || d.day_name };
          break;
        }
      }
      if (nextUp) break;
    }
    // Most recent completed
    const completed = (progress.completed_days || []).slice().sort(
      (a, b) => (b.completed_at || "").localeCompare(a.completed_at || "")
    );
    const last = completed[0];
    let lastCompleted = null;
    if (last) {
      // Find the workout title from the program tree
      const w = (program.weeks || []).find(w => w.week_number === last.week);
      const d = w?.days?.find(d => d.day_number === last.day);
      lastCompleted = {
        week: last.week,
        day: last.day,
        title: d?.workout_title || d?.day_name || "Workout",
        completedAt: last.completed_at,
      };
    }
    return { lastCompleted, nextUp };
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="pt-24 sm:pt-32 pb-24 sm:pb-12 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8 flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">MY PROGRAMS</h1>
              <p className="text-text-secondary">Start a training program to track your progress</p>
            </div>
            <div className="flex gap-1 bg-surface border border-border rounded-lg p-1">
              {[{ id: "list", icon: List }, { id: "grid", icon: LayoutGrid }].map(opt => (
                <button
                  key={opt.id}
                  onClick={() => setViewMode(opt.id)}
                  className={`p-1.5 rounded-md transition-colors ${viewMode === opt.id ? "bg-primary text-white" : "text-gray-500 hover:text-white"}`}
                  data-testid={`program-view-${opt.id}`}
                >
                  <opt.icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </motion.div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : programs.length > 0 ? (
            viewMode === "grid" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {programs.map((program) => {
                const { lastCompleted, nextUp } = computeProgramStatus(program, programProgress[program.program_id]);
                return (
                <motion.div
                  key={program.program_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-surface border border-border rounded-xl overflow-hidden hover:border-success/50 transition-all cursor-pointer"
                  onClick={() => setSelectedProgram(program)}
                  data-testid={`program-card-${program.program_id}`}
                >
                  <div className="aspect-video bg-gradient-to-br from-success/20 to-primary/20 flex items-center justify-center relative">
                    <Calendar className="w-16 h-16 text-success/50" />
                    <div className="absolute top-2 right-2 bg-success/80 text-white px-2 py-1 rounded text-xs font-medium">
                      {program.duration_weeks || program.weeks?.length || 0} weeks
                    </div>
                    {programProgress[program.program_id] && (
                      <div className="absolute bottom-2 left-2 bg-primary/80 text-white px-2 py-1 rounded text-xs font-medium">
                        In Progress
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-lg font-bold mb-2">{program.title}</h3>
                    <div className="flex flex-wrap gap-2 mb-3">
                      <span className="text-xs bg-success/20 text-success px-2 py-1 rounded capitalize">
                        {(program.goal || "general").replace("_", " ")}
                      </span>
                      <span className="text-xs bg-primary/20 text-primary px-2 py-1 rounded capitalize">
                        {program.difficulty || "intermediate"}
                      </span>
                    </div>
                    {(lastCompleted || nextUp) && (
                      <div className="space-y-1.5 mb-3 pt-3 border-t border-border" data-testid={`program-status-${program.program_id}`}>
                        {lastCompleted && (
                          <div className="flex items-start gap-2 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-success mt-1.5 flex-shrink-0" />
                            <span className="text-text-muted leading-tight">
                              <span className="text-success font-semibold">Last:</span>{" "}
                              W{lastCompleted.week}D{lastCompleted.day} · {lastCompleted.title}
                            </span>
                          </div>
                        )}
                        {nextUp ? (
                          <div className="flex items-start gap-2 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0 animate-pulse" />
                            <span className="text-text-muted leading-tight">
                              <span className="text-primary font-semibold">Next:</span>{" "}
                              W{nextUp.week}D{nextUp.day} · {nextUp.title}
                            </span>
                          </div>
                        ) : lastCompleted && (
                          <div className="flex items-start gap-2 text-xs">
                            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                            <span className="text-amber-300 font-semibold">Program complete!</span>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-text-secondary text-sm line-clamp-2">
                      {program.description || "Start your fitness journey with this program"}
                    </p>
                  </div>
                </motion.div>
                );
              })}
            </div>
            ) : (
            <div className="space-y-2">
              {programs.map((program, i) => {
                const { lastCompleted, nextUp } = computeProgramStatus(program, programProgress[program.program_id]);
                return (
                <motion.div
                  key={program.program_id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="bg-surface border border-border rounded-xl p-4 hover:border-success/50 transition-all cursor-pointer"
                  onClick={() => setSelectedProgram(program)}
                  data-testid={`program-card-${program.program_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-success/20 to-primary/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Calendar className="w-6 h-6 text-success/50" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-white truncate">{program.title}</h3>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-[10px] bg-success/20 text-success px-1.5 py-0.5 rounded capitalize">
                          {(program.goal || "general").replace("_", " ")}
                        </span>
                        <span className="text-[10px] bg-primary/20 text-primary px-1.5 py-0.5 rounded capitalize">
                          {program.difficulty || "intermediate"}
                        </span>
                        {programProgress[program.program_id] && (
                          <span className="text-[10px] bg-primary/80 text-white px-1.5 py-0.5 rounded">In Progress</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className="text-xs text-text-muted">{program.duration_weeks || program.weeks?.length || 0} wks</span>
                      <ChevronRight className="w-4 h-4 text-text-muted" />
                    </div>
                  </div>
                  {(lastCompleted || nextUp) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mt-3 pt-3 border-t border-border" data-testid={`program-status-${program.program_id}`}>
                      {lastCompleted && (
                        <div className="flex items-start gap-2 text-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-success mt-1.5 flex-shrink-0" />
                          <span className="text-text-muted leading-tight">
                            <span className="text-success font-semibold">Last:</span>{" "}
                            W{lastCompleted.week}D{lastCompleted.day} · {lastCompleted.title}
                          </span>
                        </div>
                      )}
                      {nextUp ? (
                        <div className="flex items-start gap-2 text-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0 animate-pulse" />
                          <span className="text-text-muted leading-tight">
                            <span className="text-primary font-semibold">Next:</span>{" "}
                            W{nextUp.week}D{nextUp.day} · {nextUp.title}
                          </span>
                        </div>
                      ) : lastCompleted && (
                        <div className="flex items-start gap-2 text-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400 mt-1.5 flex-shrink-0" />
                          <span className="text-amber-300 font-semibold">Program complete!</span>
                        </div>
                      )}
                    </div>
                  )}
                </motion.div>
                );
              })}
            </div>
            )
          ) : (
            <div className="bg-surface border border-border rounded-xl p-12 text-center" data-testid="programs-empty-state">
              <Calendar className="w-16 h-16 text-text-muted mx-auto mb-4" />
              <h3 className="font-heading text-xl font-bold mb-2">No Programs Yet</h3>
              <p className="text-text-secondary mb-6">Pick a program to follow, or have AI build one for you.</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/pricing"
                  className="inline-flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-semibold px-5 py-2.5 rounded-xl text-sm"
                  data-testid="programs-browse-btn">
                  Browse Programs <ChevronRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/ai-generator"
                  className="inline-flex items-center gap-2 bg-success/20 hover:bg-success/30 text-success font-semibold px-5 py-2.5 rounded-xl text-sm border border-success/40"
                  data-testid="programs-generate-btn">
                  <Dumbbell className="w-4 h-4" /> Generate with AI
                </Link>
              </div>
            </div>
          )}

          {/* Program Detail Modal */}
          <AnimatePresence>
            {selectedProgram && (
              <ProgramDetailModal 
                program={selectedProgram}
                workouts={workouts}
                programProgress={programProgress[selectedProgram.program_id]}
                onClose={() => setSelectedProgram(null)}
                onStartProgram={() => {
                  handleStartProgram(selectedProgram.program_id);
                }}
              />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};


export default ProgramsPage;
